import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtBRL, fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { TrendingUp, TrendingDown, Wallet, AlertCircle, Sparkles, BarChart3, PieChart as PieIcon, Target, ArrowUpRight, ArrowDownRight, Users, ShoppingCart, Percent } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const CAT_COLORS = ["#075BFF", "#08D5F5", "#F59E0B", "#8B5CF6", "#10B981", "#EC4899", "#06B6D4", "#F43F5E"];

function Chip({ label, value, color = "emerald", icon: Icon }) {
  const bg = { emerald: "from-emerald-500 to-emerald-600", rose: "from-rose-500 to-rose-600", teal: "from-teal-500 to-teal-600", amber: "from-amber-500 to-amber-600" }[color];
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${bg} text-white shadow-lg shadow-slate-200/60 dark:shadow-slate-950/40`}>
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
      <div className="relative flex items-center justify-between">
        <Icon className="w-5 h-5 opacity-90" />
      </div>
      <div className="relative text-xs font-semibold uppercase tracking-wider opacity-90 mt-3">{label}</div>
      <div className="relative font-display text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight">{value}</div>
    </div>
  );
}

function Gauge({ score, label }) {
  const r = 48;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 80 ? "#10B981" : score >= 60 ? "#075BFF" : score >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-5">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#E2E8F0" strokeWidth="10" className="dark:stroke-slate-800" />
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-3xl font-extrabold" style={{ color }}>{score}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">/100</div>
        </div>
      </div>
      <div>
        <div className="font-display text-xl font-extrabold" style={{ color }}>{label}</div>
        <div className="text-xs text-slate-500 mt-1 max-w-[180px] leading-snug">Equilíbrio entre receitas, despesas e inadimplência deste mês.</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500 p-8">Carregando dashboard...</div>;
  const greet = (() => { const h = new Date().getHours(); return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite"; })();
  const firstName = (user?.name || "").split(" ")[0];
  const mes = d.mes_atual.charAt(0).toUpperCase() + d.mes_atual.slice(1);

  return (
    <div className="space-y-5">
      {/* Hero / Greeting */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          {user?.logo ? (
            <img src={user.logo} alt="logo" className="w-14 h-14 rounded-2xl object-cover ring-2 ring-white dark:ring-slate-800 shadow" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-brand-cta flex items-center justify-center text-white font-display font-extrabold text-xl shadow-md shadow-[#075BFF]/30">{(firstName || "U").charAt(0)}</div>
          )}
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">{greet}, {firstName}! <span className="animate-wave">👋</span></h1>
            <div className="text-sm text-slate-500 mt-0.5">{mes}</div>
          </div>
        </div>
      </div>

      {/* Avisos inteligentes */}
      {d.avisos?.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
            <Sparkles className="w-4 h-4 text-amber-500" />Avisos inteligentes
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {d.avisos.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-800 dark:text-rose-200">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <div className="flex-1 text-sm font-medium">{a.titulo}</div>
                <div className="text-xs font-mono font-bold whitespace-nowrap">{fmtBRL(a.valor)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4 chips coloridas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Chip label="Receitas" value={fmtBRL(d.faturamento_mes)} color="emerald" icon={TrendingUp} />
        <Chip label="Despesas" value={fmtBRL(d.despesas_mes)} color="rose" icon={TrendingDown} />
        <Chip label="Saldo" value={fmtBRL(d.saldo_mes)} color="teal" icon={Wallet} />
        <Chip label="Em atraso" value={fmtBRL(d.em_atraso)} color="amber" icon={AlertCircle} />
      </div>

      {/* Saúde + Projeção */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold mb-4"><Target className="w-4 h-4 text-[#075BFF]" />Saúde Financeira</div>
          <Gauge score={d.saude_financeira} label={d.saude_label} />
        </div>
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold mb-4"><BarChart3 className="w-4 h-4 text-[#08D5F5]" />Projeção do mês</div>
          <div className="space-y-3">
            <Row label="Pedidos em carteira (não faturados)" value={fmtBRL(d.projecao_valor)} good />
            <Row label="Comissão projetada" value={fmtBRL(d.projecao_comissao)} good />
            <Row label="A receber (abertas)" value={fmtBRL(d.contas_receber_abertas)} />
            <Row label="A pagar (abertas)" value={fmtBRL(d.contas_pagar_abertas)} bad />
          </div>
        </div>
      </div>

      {/* Resumo do mês + Gastos por categoria */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="text-sm font-semibold mb-4">Resumo do mês</div>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm"><ArrowUpRight className="w-4 h-4 text-emerald-500" />Entradas</div>
              <div className="font-mono font-bold text-emerald-600">{fmtBRL(d.faturamento_mes)}</div>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm"><ArrowDownRight className="w-4 h-4 text-rose-500" />Saídas</div>
              <div className="font-mono font-bold text-rose-600">{fmtBRL(d.despesas_mes)}</div>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-sm"><Wallet className="w-4 h-4 text-blue-500" />Diferença</div>
              <div className={`font-mono font-bold ${d.saldo_mes >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtBRL(d.saldo_mes)}</div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm"><Users className="w-4 h-4 text-indigo-500" />Clientes ativos / Novos</div>
              <div className="font-mono font-bold">{fmtNum(d.clientes_ativos)} / {fmtNum(d.novos_clientes)}</div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm"><ShoppingCart className="w-4 h-4 text-amber-500" />Pedidos abertos / Faturados</div>
              <div className="font-mono font-bold">{fmtNum(d.pedidos_abertos)} / {fmtNum(d.pedidos_faturados)}</div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm"><Percent className="w-4 h-4 text-emerald-500" />Comissão a receber</div>
              <div className="font-mono font-bold text-emerald-600">{fmtBRL(d.comissoes_abertas)}</div>
            </div>
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold mb-4"><PieIcon className="w-4 h-4 text-[#075BFF]" />Gastos por categoria</div>
          {d.gastos_por_categoria.length === 0 ? <div className="text-sm text-slate-500 py-10 text-center">Sem despesas cadastradas.</div> : (
            <div className="grid grid-cols-2 gap-3 items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={d.gastos_por_categoria} dataKey="valor" nameKey="categoria" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2}>
                    {d.gastos_por_categoria.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "12px" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5">
                {d.gastos_por_categoria.map((g, i) => (
                  <div key={g.categoria} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />
                    <span className="flex-1 truncate">{g.categoria}</span>
                    <span className="font-mono font-semibold">{fmtBRL(g.valor)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Evolução de faturamento */}
      <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="font-display font-bold text-lg mb-1">Evolução de faturamento</div>
        <div className="text-xs text-slate-500 mb-4">Últimos 6 meses</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={d.serie_faturamento}>
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#075BFF" stopOpacity={0.4}/>
                <stop offset="55%" stopColor="#08D5F5" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#075BFF" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" opacity={0.6} />
            <XAxis dataKey="mes" fontSize={12} stroke="#64748B" />
            <YAxis fontSize={12} stroke="#64748B" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px" }} />
            <Area type="monotone" dataKey="valor" stroke="#075BFF" fill="url(#g)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Row({ label, value, good, bad }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className="text-sm text-slate-600 dark:text-slate-300">{label}</div>
      <div className={`font-mono font-bold ${good ? "text-emerald-600 dark:text-emerald-400" : bad ? "text-rose-600 dark:text-rose-400" : "text-slate-900 dark:text-white"}`}>{value}</div>
    </div>
  );
}
